import { useCallback, useEffect, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  EventName,
  useBridgeAPI,
  useBridgeState,
  useWebViewPostMessage,
} from '@digitaz/bridge-sdk-client';
import { usePostMessage } from '@/hooks/usePostMessage';
import { openNewWindow } from '@/utils/helpers';
import {
  CREATE_CARD,
  DELETE_CARD,
  SET_PRIMARY_CARD,
  UPDATE_CARD,
} from '@/api/mutations/cards.mutations';
import { GET_PROFILE_USER_CARDS } from '@/api/queries/cards.queries';
import { GET_USER } from '@/api/queries/user.queries';
import {
  CreateCardMutation,
  DeleteCardMutation,
  DeleteCardMutationVariables,
  ProfileUserCardsQuery,
  SavedCard,
  SetPrimaryCardMutation,
  SetPrimaryCardMutationVariables,
  UpdateCardMutation,
  UpdateCardMutationVariables,
} from '@/api/types/cards.types';
import { markPaymentStatusReactOpener } from '../helpers/paymentStatusOpener';

enum PostMessageTypes {
  Success = 'NewCardSuccess',
  Failed = 'NewCardFailed',
}

type PaymentStatusParams = {
  type: PostMessageTypes;
  origin: string;
};

export type CardNotifyVariant = 'success' | 'error' | 'addFailed';

type UseUserCardsParams = {
  onNotify?: (message: string, variant: CardNotifyVariant) => void;
};

type UseUserCardsResult = {
  cards: SavedCard[];
  isLoading: boolean;
  isError: boolean;
  addCard: () => void;
  updateCard: (
    id: number,
    card: Partial<Pick<SavedCard, 'name' | 'isActive'>>,
    onSuccess?: () => void
  ) => void;
  deleteCard: (id: number, onSuccess?: () => void) => void;
  setPrimaryCard: (id: number, onSuccess?: () => void) => void;
  refetchCards: () => Promise<void>;
};

const isBrowser = typeof window !== 'undefined';

export const useUserCards = ({
  onNotify,
}: UseUserCardsParams = {}): UseUserCardsResult => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { isWebView } = useBridgeState();
  const bridgeAPI = useBridgeAPI();

  const [cards, setCards] = useState<SavedCard[]>([]);
  const [isUnavailable, setIsUnavailable] = useState(false);

  const { data, loading, error, refetch } = useQuery<ProfileUserCardsQuery>(
    GET_PROFILE_USER_CARDS,
    {
      skip: !isBrowser,
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    if (data?.cards) {
      setCards(data.cards);
      setIsUnavailable(false);
      return;
    }

    if (!loading && !error && data !== undefined) {
      setIsUnavailable(true);
    }
  }, [data, loading, error]);

  const [createCardMutation] = useMutation<CreateCardMutation>(CREATE_CARD);
  const [updateCardMutation] = useMutation<
    UpdateCardMutation,
    UpdateCardMutationVariables
  >(UPDATE_CARD);
  const [deleteCardMutation] = useMutation<
    DeleteCardMutation,
    DeleteCardMutationVariables
  >(DELETE_CARD);
  const [setPrimaryCardMutation] = useMutation<
    SetPrimaryCardMutation,
    SetPrimaryCardMutationVariables
  >(SET_PRIMARY_CARD);

  function getCardNotifyLabel(card: SavedCard) {
    return card.pan ? card.pan.slice(-6) : card.name;
  }

  const refetchCards = useCallback(async () => {
    const { data: updatedData } = await refetch();

    void client.refetchQueries({ include: [GET_USER] }).catch(() => undefined);

    if (updatedData?.cards) {
      setCards(updatedData.cards);
      setIsUnavailable(false);
      return;
    }

    setIsUnavailable(true);
  }, [client, refetch]);

  const handleCardAdded = useCallback(async () => {
    try {
      await refetchCards();
    } catch {
      // Payment already succeeded; keep going so the user still gets feedback.
    }

    onNotify?.(t('pages.cabinet.card_added'), 'success');
  }, [onNotify, refetchCards, t]);

  const handleCardAddFailed = useCallback(() => {
    onNotify?.(t('pages.cabinet.errors.save_failed'), 'addFailed');
  }, [onNotify, t]);

  const handlePostMessage = useCallback(
    (postMessageName: string) => {
      if (postMessageName === PostMessageTypes.Success) {
        handleCardAdded();
        return;
      }

      if (postMessageName === PostMessageTypes.Failed) {
        handleCardAddFailed();
      }
    },
    [handleCardAddFailed, handleCardAdded]
  );

  usePostMessage({
    postMessageName: PostMessageTypes.Success,
    handler: handlePostMessage,
  });

  usePostMessage({
    postMessageName: PostMessageTypes.Failed,
    handler: handlePostMessage,
  });

  useWebViewPostMessage<EventName.PaymentStatus, PaymentStatusParams>({
    eventName: EventName.PaymentStatus,
    handler: params => {
      if (params.type === PostMessageTypes.Success) {
        handleCardAdded();
        return;
      }

      if (params.type === PostMessageTypes.Failed) {
        handleCardAddFailed();
      }
    },
  });

  const addCard = () => {
    const newWindow = isWebView
      ? null
      : openNewWindow({ width: 505, height: 500 });

    createCardMutation()
      .then(response => {
        const createCardData = response.data?.createCard;
        const redirectUrl = createCardData?.entity?.redirectUrl;
        if (!redirectUrl || (createCardData?.errors?.length ?? 0) > 0) {
          newWindow?.close();
          return;
        }

        markPaymentStatusReactOpener();

        if (isWebView) {
          window.sessionStorage.setItem(
            'paymentReturnTo',
            window.location.href
          );
          bridgeAPI.openPayment(redirectUrl);
          return;
        }

        newWindow?.location.replace(redirectUrl);
      })
      .catch(() => {
        newWindow?.close();
        handleCardAddFailed();
      });
  };

  const updateCard = (
    id: number,
    card: Partial<Pick<SavedCard, 'name' | 'isActive'>>,
    onSuccess?: () => void
  ) => {
    const cardToRename = cards.find(item => item.id === id);
    if (!cardToRename) return;

    const { name, isActive } = card;

    updateCardMutation({
      variables: {
        id,
        ...(name !== undefined && { name }),
        ...(isActive !== undefined && { active: isActive }),
      },
    })
      .then(async response => {
        const updateCardData = response.data?.updateCard;
        if (
          !updateCardData?.entity?.card ||
          updateCardData.entity.status !== 'SUCCESS' ||
          (updateCardData.errors?.length ?? 0) > 0
        ) {
          throw new Error('Broken response');
        }

        await Promise.allSettled([refetchCards()]);
        onNotify?.(
          t('pages.cabinet.card_is_renamed', {
            name: getCardNotifyLabel(cardToRename),
          }),
          'success'
        );
        onSuccess?.();
      })
      .catch(() => {
        onNotify?.(t('pages.cabinet.errors.rename_failed'), 'error');
      });
  };

  const deleteCard = (id: number, onSuccess?: () => void) => {
    const card = cards.find(item => item.id === id);
    if (!card) return;

    deleteCardMutation({
      variables: { id },
    })
      .then(async response => {
        const deleteCardData = response.data?.deleteCard;
        if (
          !deleteCardData?.entity ||
          (deleteCardData.errors?.length ?? 0) > 0
        ) {
          throw new Error('Broken response');
        }

        await Promise.allSettled([refetchCards()]);
        onNotify?.(
          t('pages.cabinet.card_is_removed', {
            name: getCardNotifyLabel(card),
          }),
          'success'
        );
        onSuccess?.();
      })
      .catch(() => {
        onNotify?.(t('pages.cabinet.errors.delete_failed'), 'error');
      });
  };

  const setPrimaryCard = (id: number, onSuccess?: () => void) => {
    const card = cards.find(item => item.id === id);
    if (!card) return;

    setPrimaryCardMutation({
      variables: { id },
    })
      .then(async response => {
        const primaryCardData = response.data?.setPrimary;
        if (
          !primaryCardData?.entity ||
          (primaryCardData.errors?.length ?? 0) > 0
        ) {
          throw new Error('Broken response');
        }

        await Promise.allSettled([refetchCards()]);
        onNotify?.(
          t('pages.cabinet.card_is_selected_as_main', {
            name: getCardNotifyLabel(card),
          }),
          'success'
        );
        onSuccess?.();
      })
      .catch(() => {
        onNotify?.(t('pages.cabinet.errors.set_primary_failed'), 'error');
      });
  };

  return {
    cards,
    isLoading: !isBrowser || loading,
    isError: !!error || isUnavailable,
    addCard,
    updateCard,
    deleteCard,
    setPrimaryCard,
    refetchCards,
  };
};
